import { useCallback, useEffect, useRef, useState } from 'react';

const INK = '#0D2240';
const MAX_W = 600;
const MAX_H = 200;

/** Copy a region of `src` onto a new canvas no bigger than MAX_W × MAX_H. */
function fitToCanvas(src, sx, sy, sw, sh) {
  const scale = Math.min(1, MAX_W / sw, MAX_H / sh);
  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(sw * scale));
  out.height = Math.max(1, Math.round(sh * scale));
  out.getContext('2d').drawImage(src, sx, sy, sw, sh, 0, 0, out.width, out.height);
  return out;
}

/**
 * Turn an uploaded scan/photo of a signature into a transparent PNG data URL:
 * paper-white pixels become transparent and the image is trimmed to the ink.
 */
export function signatureImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const work = document.createElement('canvas');
      const scale = Math.min(1, 1200 / img.width);
      work.width = Math.round(img.width * scale);
      work.height = Math.round(img.height * scale);
      const ctx = work.getContext('2d');
      ctx.drawImage(img, 0, 0, work.width, work.height);
      const px = ctx.getImageData(0, 0, work.width, work.height);
      const d = px.data;
      let minX = work.width, minY = work.height, maxX = -1, maxY = -1;
      for (let i = 0; i < d.length; i += 4) {
        const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
        if (lum > 200 || d[i + 3] < 20) { d[i + 3] = 0; continue; }
        const p = i / 4, x = p % work.width, y = Math.floor(p / work.width);
        if (x < minX) minX = x; if (x > maxX) maxX = x;
        if (y < minY) minY = y; if (y > maxY) maxY = y;
      }
      if (maxX < 0) { reject(new Error('No signature found in that image — try a darker, clearer scan.')); return; }
      ctx.putImageData(px, 0, 0);
      const pad = 6;
      const sx = Math.max(0, minX - pad), sy = Math.max(0, minY - pad);
      resolve(fitToCanvas(work, sx, sy, Math.min(work.width, maxX + pad) - sx, Math.min(work.height, maxY + pad) - sy).toDataURL('image/png'));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('That file could not be read as an image.')); };
    img.src = url;
  });
}

/**
 * Draw-a-signature box for finger, stylus or mouse. Calls onChange with a PNG
 * data URL of the strokes (trimmed to them), or null once cleared.
 */
export default function SignaturePad({ onChange, height = 170 }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const bounds = useRef(null);
  const width = useRef(0);
  const [empty, setEmpty] = useState(true);

  const setup = useCallback(() => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    width.current = r.width;
    c.width = Math.round(r.width * dpr);
    c.height = Math.round(r.height * dpr);
    const ctx = c.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2.6;
    ctx.strokeStyle = INK;
    bounds.current = null;
    setEmpty(true);
  }, []);

  const clear = useCallback(() => { setup(); onChange(null); }, [setup, onChange]);

  useEffect(() => {
    setup();
    // Resizing a canvas wipes it, so only react to real width changes (not a
    // phone's address bar sliding in and out).
    const onResize = () => {
      if (Math.abs(canvasRef.current.getBoundingClientRect().width - width.current) > 1) clear();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [setup, clear]);

  const point = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const grow = ({ x, y }) => {
    const b = bounds.current;
    bounds.current = b
      ? { minX: Math.min(b.minX, x), minY: Math.min(b.minY, y), maxX: Math.max(b.maxX, x), maxY: Math.max(b.maxY, y) }
      : { minX: x, minY: y, maxX: x, maxY: y };
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    drawing.current = true;
    const p = point(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(p.x + 0.1, p.y + 0.1);
    ctx.stroke();
    grow(p);
  };
  const onPointerMove = (e) => {
    if (!drawing.current) return;
    const p = point(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    grow(p);
  };
  const onPointerUp = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const b = bounds.current;
    if (!b) return;
    setEmpty(false);
    const dpr = window.devicePixelRatio || 1;
    const c = canvasRef.current;
    const pad = 6;
    const sx = Math.max(0, (b.minX - pad) * dpr), sy = Math.max(0, (b.minY - pad) * dpr);
    const sw = Math.min(c.width, (b.maxX + pad) * dpr) - sx, sh = Math.min(c.height, (b.maxY + pad) * dpr) - sy;
    onChange(fitToCanvas(c, sx, sy, sw, sh).toDataURL('image/png'));
  };

  return (
    <div>
      <div className="relative rounded-[10px] border-2 border-dashed border-[#D1D5DB] bg-white">
        <canvas
          ref={canvasRef}
          style={{ height, touchAction: 'none' }}
          className="block w-full cursor-crosshair rounded-[10px]"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          aria-label="Signature drawing area"
        />
        {empty && (
          <p className="pointer-events-none absolute inset-0 flex items-center justify-center text-[13px] text-[#9CA3AF]">
            Sign here with your finger or mouse
          </p>
        )}
        <div className="pointer-events-none absolute left-5 right-5 bottom-8 border-b border-[#E5E7EB]" />
      </div>
      <div className="flex justify-end mt-1.5">
        <button type="button" onClick={clear} disabled={empty}
          className="text-[12px] font-semibold text-[#6B7280] hover:text-[#111827] disabled:opacity-40">
          Clear
        </button>
      </div>
    </div>
  );
}
